import { useEffect } from 'react';
import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { usePpdfSouhlasyLocale } from '@gov-nx/module/page';
import { useFiltersStoreFilters } from '@gov-nx/store';
import { today, toStringDate, useDateShape } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type {
	PpdfConsentsDataRequest,
	PpdfConsentsFilterType,
	PpdfConsentsFormData,
} from './context.types';

export interface FormInstanceProps {
	code: PageCode;
	defaultValues: PpdfConsentsFormData;
}

export function FormInstance({
	code,
	defaultValues,
}: FormInstanceProps): FormDefinition<PpdfConsentsFormData> {
	const { getDatePickerShape } = useDateShape();
	const { t } = usePpdfSouhlasyLocale();
	const filters = useFiltersStoreFilters<PpdfConsentsFilterType>(code);

	const formSchema = yup
		.object({
			text: yup.string().optional(),
			od: getDatePickerShape({
				isInvalidMessage: t('filtr.od.validace.invalid'),
			})
				.max(today(), t('filtr.do.validace.max'))
				.nullable(),
			do: getDatePickerShape({
				isInvalidMessage: t('filtr.do.validace.invalid'),
			})
				.max(today(), t('filtr.do.validace.max'))
				.nullable()
				.min(yup.ref('od'), t('filtr.od.validace.older')),
		})
		.required();

	const formMethods = useGovForm<PpdfConsentsFormData>({
		formSchema,
		defaultValues,
	});

	useEffect(() => {
		filters?.text && formMethods.setValue('text', filters.text);
		filters?.od && formMethods.setValue('od', filters.od);
		filters?.do && formMethods.setValue('do', filters.do);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (
	filter: PpdfConsentsFilterType
): PpdfConsentsDataRequest => ({
	text: filter.text,
	od: filter.od ? toStringDate(filter.od) : undefined,
	do: filter.do ? toStringDate(filter.do) : undefined,
	start: filter.start,
	pocet: filter.pageSize,
});
